import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { FC, useState } from 'react';

interface PdfPreviewDialogProps {
  open: boolean;
  url: string | null;
  title?: string;
  onClose: () => void;
}

const PdfPreviewDialog: FC<PdfPreviewDialogProps> = ({ open, url, title, onClose }) => {
  const [fullScreen, setFullScreen] = useState(false);

  if (!url) return null;

  const openInNewTab = () => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth='lg'
      fullWidth
      PaperProps={{ sx: { height: fullScreen ? '100%' : '90vh' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}>
        <Typography variant='subtitle1' fontWeight='600' noWrap title={title}>
          {title || 'Document Preview'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size='small'
            variant='outlined'
            startIcon={fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            onClick={() => setFullScreen((v) => !v)}
            sx={{ textTransform: 'capitalize' }}>
            {fullScreen ? 'Exit full screen' : 'Full screen'}
          </Button>
          <Button
            size='small'
            variant='outlined'
            startIcon={<OpenInNewIcon />}
            onClick={openInNewTab}
            sx={{ textTransform: 'capitalize' }}>
            Open in new tab
          </Button>
          <Button
            size='small'
            variant='outlined'
            startIcon={<DownloadIcon />}
            component='a'
            href={url}
            download
            target='_blank'
            rel='noopener noreferrer'
            sx={{ textTransform: 'capitalize' }}>
            Download
          </Button>
          <Tooltip title='Close'>
            <IconButton size='small' aria-label='close preview' onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'grey.100' }}>
        <iframe
          src={url}
          title={title || 'Document preview'}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      </Box>
    </Dialog>
  );
};

export default PdfPreviewDialog;
